import { isNull } from "lodash";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

// Imported assets
import { HideIcon, UnhideIcon } from "../../../assets/icons/svgs/Icons";
import StrataLogo from "../../../assets/images/STRATALogo_Black.png";

// Imported components
import ForgotPasswordModal from "./components/ForgotPasswordModal";
import Info from "../../../components/form/Info";
import Label from "../../../components/form/Label";
import PrimaryButton from "../../../components/ui/PrimaryButton";
import SecondaryButton from "../../../components/ui/SecondaryButton";

// Imported contexts
import { useUserContext } from "../../../contexts/User";

// Styled components
const CancelButton = styled(SecondaryButton)`
  background: white;
  width: 120px;

  &:hover {
    background: lab(from white calc(l - 5) a b / 100);
  }
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  width: 100vw;

  & * {
    font-family: Inter, Oxygen, Roboto, sans-serif !important;
  }
`;

const ErrorInfo = styled(Info)`
  color: red;
  font-size: 14px;
  margin-top: 4px;
  text-align: center;
  width: 100%;
`;

const FormField = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: flex-start;
  min-width: 280px;
  position: relative;
  width: 25vw;
`;

const FormRow = styled.div`
  align-items: flex-start;
  align-self: stretch;
  display: flex;
  flex-direction: row;
  gap: 80px;
  justify-content: center;
  margin: 10px 0;
  position: relative;
`;

const LoginButton = styled(PrimaryButton)`
  background: var(--terbine-blue);
  width: 120px;

  &:hover:enabled {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const LoginForm = styled.form`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 1000px;
  padding: 20px;
  width: 100%;
`;

const Logo = styled.img`
  display: block;
  height: auto;
  margin-bottom: 10px;
  object-fit: contain;
  width: min(55vw, 400px);
`;

const ShowPasswordButton = styled.div`
  cursor: pointer;
`;

const Subtitle = styled.div`
  font-family: Inter, Oxygen, Roboto, sans-serif !important;
  font-size: clamp(12px, 2.5vw, 18px);
`;

const Login = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [passwordErr, setPasswordErr] = useState<string | null>(null);

  const [otherErr, setOtherErr] = useState<string | null>(null);

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const {
    isAuthenticated,
    // emailMax,
    passwordMin,
    // passwordMax,
    userLogin,
    validateInputs,
  } = useUserContext();
  const navigate = useNavigate();
  const { state } = useLocation();

  // Redirect to map view page if already logged in
  useEffect(() => {
    if (isAuthenticated) navigate("/dashboard");
  }, [isAuthenticated, navigate]);

  // Handle login
  const login = async (e) => {
    e?.preventDefault();

    setOtherErr(null);

    let errors = validateInputs("Login", {
      email,
      password,
    });

    setEmailErr(errors.emailErr);
    setPasswordErr(errors.passwordErr);

    if (isNull(emailErr) && isNull(passwordErr)) {
      await userLogin(email, password)
        .then((resp) => {
          navigate(state?.path || "/dashboard");
        })
        .catch((err) => {
          setOtherErr(err.response.data.errorMessage);
        });
    }
  };

  const togglePassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [setShowPassword, showPassword]);

  // Render
  return (
    <Container>
      <LoginForm aria-label="Login Form" onSubmit={(e) => login(e)}>
        <FormRow
          style={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Logo
            alt="Strata Logo"
            aria-label="Strata Logo"
            src={StrataLogo}
          />
          <Subtitle>Mobility Infrastructure Supervisory Platform</Subtitle>
        </FormRow>
        <FormRow>
          <FormField>
            <Label>Email</Label>
            <FormControl sx={{ width: "100%" }}>
              <TextField
                error={emailErr || otherErr ? true : false}
                onChange={(e) => setEmail(e.target.value)}
                size="small"
                slotProps={{
                  htmlInput: {
                    // maxLength: emailMax,
                  },
                }}
                value={email}
                variant="outlined"
              />
            </FormControl>
            {emailErr && <ErrorInfo>{emailErr}</ErrorInfo>}
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <Label>Password</Label>
            <FormControl sx={{ width: "100%" }}>
              <TextField
                error={passwordErr || otherErr ? true : false}
                onChange={(e) => setPassword(e.target.value)}
                size="small"
                slotProps={{
                  htmlInput: {
                    // maxLength: passwordMax,
                    minLength: passwordMin,
                  },
                  input: {
                    endAdornment: (
                      <ShowPasswordButton onClick={() => togglePassword()}>
                        {showPassword ? <UnhideIcon /> : <HideIcon />}
                      </ShowPasswordButton>
                    ),
                  },
                }}
                type={showPassword ? "text" : "password"}
                value={password}
                variant="outlined"
              />
            </FormControl>
            {passwordErr && <ErrorInfo>{passwordErr}</ErrorInfo>}
          </FormField>
        </FormRow>

        {otherErr && (
          <FormRow>
            <ErrorInfo>{otherErr}</ErrorInfo>
          </FormRow>
        )}

        <FormRow style={{ gap: "40px" }}>
          <LoginButton
            aria-label="Login Button"
            onClick={(e) => login(e)}
            type="submit"
          >
            Login
          </LoginButton>

          <CancelButton
            aria-label="Cancel Button"
            onClick={() => null}
            type="button"
          >
            Cancel
          </CancelButton>
        </FormRow>

        <FormRow>
          <ForgotPasswordModal />
        </FormRow>
      </LoginForm>
    </Container>
  );
};

export default Login;
